import { ILandholderDetailsModel, LandholderDetailsModel, AddressModel, IReferenceDataModel } from '../models/veg-wizard-models';
import { GlobalValidationMessageComponent } from 'src/app/shared/global-validation-message/global-validation-message.component';
import { CommonModule } from '@angular/common';
import { BaseWizardComponent } from '../base-wizard.component';
import { VegWizardDataService } from '../services/veg-wizard-data.service';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReferenceDataService } from '../services/reference-data-service';
import { first, mergeMap, tap } from 'rxjs';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { VegValidators } from '../veg-validators/veg-validators';
import { ReferencesEnum } from 'src/app/models/shared-models';
import { UtilService } from '../services/util.service';

@UntilDestroy()
@Component({
  selector: 'app-landholder-details',
  templateUrl: './landholder-details.component.html',
  styleUrls: ['./landholder-details.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GlobalValidationMessageComponent
  ],
})

export class LandholderDetailsComponent extends BaseWizardComponent implements OnInit {
  form: FormGroup;
  landholderDetails: ILandholderDetailsModel | undefined;

  states: IReferenceDataModel[] = [];
  DEFAULT_COUNTRY_AUSTRALIA_ID: number = ReferencesEnum.australiaCountryId;
  DEFAULT_STATE_QLD_ID: number = ReferencesEnum.queenslandStateId;
  nextButtonClicked = false;

  // form getters
  get firstNameControl() { return this.form.controls['firstName']; }
  get lastNameControl() { return this.form.controls['lastName']; }
  get middleNameControl() { return this.form.controls['middleName']; }

  get companyNameControl() { return this.form.controls['companyName']; }
  get phoneControl() { return this.form.controls['phone']; }
  get mobileControl() { return this.form.controls['mobile']; }
  get emailControl() { return this.form.controls['email']; }
  get homeAddressLine1Control() { return this.form.controls['homeAddressLine1']; }
  get homeAddressLine2Control() { return this.form.controls['homeAddressLine2']; }
  get homeAddressSuburbControl() { return this.form.controls['homeAddressSuburb']; }
  get homeAddressPostcodeControl() { return this.form.controls['homeAddressPostcode']; }
  get homeAddressStateControl() { return this.form.controls['homeAddressStateId']; }

  get mailingAddressSameAsPhysicalAddressControl() { return this.form.controls['mailingAddressSameAsPhysicalAddress']; }
  get mailingAddressLine1Control() { return this.form.controls['mailingAddressLine1']; }
  get mailingAddressLine2Control() { return this.form.controls['mailingAddressLine2']; }
  get mailingAddressSuburbControl() { return this.form.controls['mailingAddressSuburb']; }
  get mailingAddressPostcodeControl() { return this.form.controls['mailingAddressPostcode']; }
  get mailingAddresStateControl() { return this.form.controls['mailingAddressStateId']; }

  constructor(
    private formBuilder: FormBuilder,
    private vwds: VegWizardDataService,
    private referenceDataService: ReferenceDataService,
    private utilService: UtilService,
    private cdRef: ChangeDetectorRef,
    router: Router
  ) {
    super();

    this.landholderDetails = this.vwds.getLandholderDetailsModel();

    this.form = this.formBuilder.group({
      firstName: [this.landholderDetails?.firstName, [Validators.required, Validators.maxLength(20)]],
      middleName: [this.landholderDetails?.middleName],
      lastName: [this.landholderDetails?.lastName, [Validators.required]],

      companyName: [this.landholderDetails?.companyName, [Validators.maxLength(255)]],

      phone: [this.landholderDetails?.phone],
      mobile: [this.landholderDetails?.mobile],
      email: [this.landholderDetails?.email, [Validators.required, VegValidators.email]],

      homeAddressLine1: [this.landholderDetails?.homeAddress?.line1, [Validators.required]],
      homeAddressLine2: [this.landholderDetails?.homeAddress?.line2],
      homeAddressSuburb: [this.landholderDetails?.homeAddress?.suburb, [Validators.required]],
      homeAddressPostcode: [this.landholderDetails?.homeAddress?.postcode, [Validators.required, VegValidators.postcode]],
      homeAddressStateId: [this.landholderDetails?.homeAddress?.stateId === undefined ? this.DEFAULT_STATE_QLD_ID : this.landholderDetails?.homeAddress?.stateId],

      mailingAddressSameAsPhysicalAddress: [this.landholderDetails?.mailingAddressSameAsPhysicalAddress ?? true],

      mailingAddressLine1: [this.landholderDetails?.mailingAddress?.line1],
      mailingAddressLine2: [this.landholderDetails?.mailingAddress?.line2],
      mailingAddressSuburb: [this.landholderDetails?.mailingAddress?.suburb],
      mailingAddressPostcode: [this.landholderDetails?.mailingAddress?.postcode],
      mailingAddressStateId: [this.landholderDetails?.mailingAddress?.stateId === undefined ? this.DEFAULT_STATE_QLD_ID : this.landholderDetails?.mailingAddress?.stateId],
    });

  }

  ngOnInit(): void {
    this.cdRef.detectChanges();

    this.referenceDataService.getStates$()
      .pipe(first(), untilDestroyed(this),
        tap(results => {
          this.states = results;
          this.DEFAULT_STATE_QLD_ID = results.filter(x => x.name == ReferencesEnum.queenslandState)[0].id ?? -1;
        }), mergeMap(x =>
          this.referenceDataService.getCountries$()
            .pipe(tap(result => {
              this.DEFAULT_COUNTRY_AUSTRALIA_ID = result.filter(x => x.name == ReferencesEnum.australiaCountry)[0].id ?? -1;
            }))
        )).subscribe();

    // Add form wide validation.
    this.form.setValidators([this.atLeastOnePhoneNumberValidator()]);

    // Setting and clearing the validators of the mailing address fields so that the form doesn't get stuck when they're hidden
    // and clearing the mailing address values.
    this.mailingAddressSameAsPhysicalAddressControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(areAddressesEqual => {

        if (!areAddressesEqual) {
          this.mailingAddressLine1Control?.setValidators([Validators.required]);
          this.mailingAddressSuburbControl?.setValidators([Validators.required]);
          this.mailingAddressPostcodeControl?.setValidators([Validators.required, VegValidators.postcode]);
        }
        else {
          this.mailingAddressLine1Control.clearValidators();
          this.mailingAddressSuburbControl.clearValidators();
          this.mailingAddressPostcodeControl.clearValidators();

          const patch = {
            mailingAddressLine1: '',
            mailingAddressLine2: '',
            mailingAddressSuburb: '',
            mailingAddressPostcode: '',
            mailingAddressStateId: undefined,
            mailingAddressCountryId: undefined
          };
          this.form.patchValue(patch);
        }

        this.mailingAddressLine1Control?.updateValueAndValidity();
        this.mailingAddressPostcodeControl?.updateValueAndValidity();
        this.mailingAddressSuburbControl?.updateValueAndValidity();
      });

  }

  atLeastOnePhoneNumberValidator(): ValidatorFn {
    return () => {
      const mobile = this.mobileControl;
      const phone = this.phoneControl;

      if (!this.mobileControl && !this.phoneControl) { return { atLeastOnePhoneRequiredValidationError: true } }

      if (!VegValidators.phoneNumber(this.mobileControl) || !VegValidators.phoneNumber(this.phoneControl)) { return null; }

      return mobile?.value?.length || phone?.value?.length ? null : { atLeastOnePhoneRequiredValidationError: true }
    };
  }

  private getLandholderDetails(form: FormGroup): ILandholderDetailsModel {
    const model = new LandholderDetailsModel();

    model.email = this.emailControl.value;
    model.companyName = this.companyNameControl.value;
    model.firstName = this.firstNameControl.value;
    model.middleName = this.middleNameControl.value;
    model.lastName = this.lastNameControl.value;
    model.phone = this.phoneControl.value;
    model.mobile = this.mobileControl.value;
    model.canAttachEvidenceOfConsent = false;
    model.evidenceOfConsentFileNames = [];

    model.homeAddress = new AddressModel();
    model.homeAddress.line1 = this.homeAddressLine1Control.value;
    model.homeAddress.line2 = this.homeAddressLine2Control.value;
    model.homeAddress.postcode = this.homeAddressPostcodeControl.value;
    model.homeAddress.suburb = this.homeAddressSuburbControl.value;
    model.homeAddress.stateId = this.homeAddressStateControl.value ?? this.DEFAULT_STATE_QLD_ID;
    model.homeAddress.countryId = this.DEFAULT_COUNTRY_AUSTRALIA_ID;

    model.mailingAddress = new AddressModel();
    model.mailingAddress.line1 = this.mailingAddressLine1Control.value;
    model.mailingAddress.line2 = this.mailingAddressLine2Control.value;
    model.mailingAddress.postcode = this.mailingAddressPostcodeControl.value;
    model.mailingAddress.suburb = this.mailingAddressSuburbControl.value;
    model.mailingAddress.stateId = this.mailingAddresStateControl.value ?? this.DEFAULT_STATE_QLD_ID;
    model.mailingAddress.countryId = this.DEFAULT_COUNTRY_AUSTRALIA_ID;

    model.mailingAddressSameAsPhysicalAddress = this.mailingAddressSameAsPhysicalAddressControl.value;
    return model;
  }

  override next(): void {

    this.form.updateValueAndValidity();

    if (this.form.invalid) {
      this.utilService.getFormValidationErrors(this.form);
      this.cdRef.detectChanges();
      this.nextButtonClicked = true; // set flag to true to trigger validation
      return;
    }

    this.landholderDetails = this.getLandholderDetails(this.form);

    this.vwds
      .setLandholderDetailsModel$(this.landholderDetails)
      .pipe(first(), untilDestroyed(this))
      .subscribe((success) => {
        if (success) {
          super.next();
        }
      });
  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }

  ngAfterViewInit() {
    this.cdRef.detectChanges();
  }

  formatPostCode(event: KeyboardEvent) {
    const charCode = event.keyCode

    if ((charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    } else {
      return true;
    }
  }
}
